import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GenericSvg } from "../../assets/svgs/GenericSVG";
import { CustomButton } from "../../core/components/buttons/CustomButton";
import { InfoText } from "../../core/components/texts/InfoText";
import {
  getAppointmentPatientIsSelfPayer,
  getAppointmentSelfPayerAdditionalTreatments,
} from "../../core/store/appointmentSlice";
import { getCurrentPracticeId } from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { H4 } from "../../core/styles/stringStyles";
import { useStyles } from "../../core/styles/theme";
import { hexWithOpacity } from "../../core/utils/helpers";
import { useHandleParamNavigation } from "../../core/utils/useHandleParamNavigation";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import {
  formatAdditionalTreatmentLabel,
  getSelfPayerCatalogue,
} from "./utils/selfPayerTreatmentOptions";

export const AppointmentSelfPayerAdditionalTreatmentsScreen = () => {
  const { t } = useTranslation();
  const theme = useStyles();
  const navigate = useHandleParamNavigation();
  const practiceId = useMainStore(getCurrentPracticeId);
  const { additionalTreatments } = getSelfPayerCatalogue(practiceId);
  const isSelfPayer = useMainStore(getAppointmentPatientIsSelfPayer);
  const selected = useMainStore(getAppointmentSelfPayerAdditionalTreatments);
  const setAdditionalTreatments = useMainStore(
    (state) => state.setAppointmentSelfPayerAdditionalTreatments,
  );

  // The route stays reachable by URL and must not strand a patient who never chose self-payer.
  useEffect(() => {
    if (isSelfPayer) return;
    navigate("/appointment/recipeInfo", { replace: true });
  }, [isSelfPayer, navigate]);

  if (!isSelfPayer) return null;

  // Rebuilt from the catalogue rather than appended to, so the note text is click-order-independent.
  const toggle = (name: string) => {
    const current = selected ?? [];
    const next = additionalTreatments
      .map((entry) => entry.name)
      .filter((entry) =>
        entry === name ? !current.includes(name) : current.includes(entry),
      );
    setAdditionalTreatments(next.length ? next : undefined);
  };

  return (
    <AppointmentScreenWrapper>
      <InfoText
        headline={t("APPOINTMENT_SELF_PAYER_ADDITIONAL_TREATMENTS_HEADLINE")}
        text={[t("APPOINTMENT_SELF_PAYER_ADDITIONAL_TREATMENTS_DESCRIPTION")]}
      />
      <OptionsContainer>
        {additionalTreatments.map((option, index) => {
          const isSelected = selected?.includes(option.name) ?? false;
          return (
            <Fragment key={option.name}>
              <OptionButton onClick={() => toggle(option.name)}>
                <GenericSvg
                  name={
                    isSelected ? "CheckBoxSquareActive" : "CheckBoxSquareInactive"
                  }
                  width={24}
                  height={24}
                  primaryColor={
                    isSelected
                      ? theme.colors.white
                      : hexWithOpacity(theme.colors.dark, 0.5)
                  }
                  secondaryColor={isSelected ? theme.colors.dark : undefined}
                />
                <OptionLabel>
                  {formatAdditionalTreatmentLabel(option.name, option.priceLabel)}
                </OptionLabel>
              </OptionButton>
              {index < additionalTreatments.length - 1 && <Divider />}
            </Fragment>
          );
        })}
      </OptionsContainer>
    </AppointmentScreenWrapper>
  );
};

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionButton = styled(CustomButton)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
`;

const OptionLabel = styled(H4)`
  color: ${(props) => props.theme.colors.dark};
  text-align: left;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) =>
    hexWithOpacity(props.theme.colors.medium, 0.1)};
  margin: 10px 0;
`;
